import type { AppConfig } from "../types/config";
import { defaultAppConfig, defaultAlertRules, DEFAULT_TARGET_STINT_MINUTES } from "./gauges";

const REMOVED_GAUGE_IDS = new Set(["speed", "throttle", "gps-speed"]);
const MIGRATED_GAUGE_IDS = new Set(["iat", "fuel"]);

const KNOWN_CONFIG_KEYS: ReadonlySet<keyof AppConfig> = new Set<keyof AppConfig>([
  "mqtt",
  "server",
  "gauges",
  "calibration",
  "finishLine",
  "sectorLines",
  "alerts",
  "analysis",
  "targetStintMinutes",
]);

/** Drop sections the app no longer knows about (configs saved by retired features). */
export function stripUnknownConfigKeys(input: AppConfig): AppConfig {
  const copy: AppConfig = { ...input };
  for (const key of Object.keys(copy) as (keyof AppConfig)[]) {
    if (!KNOWN_CONFIG_KEYS.has(key)) delete copy[key];
  }
  return copy;
}

/**
 * Bring a persisted AppConfig up to the current shape.
 *
 * Pure and idempotent: applying it twice yields the same result. Kept out of
 * the fetch hook so each rule can be unit-tested and so the history of shape
 * changes lives in one place.
 */
export function migrateAppConfig(input: AppConfig): AppConfig {
  const data: AppConfig = stripUnknownConfigKeys(input);
  const afrDefault = defaultAppConfig.gauges.find((g) => g.id === "afr");

  if (data.gauges) {
    data.gauges = data.gauges
      .filter((g) => !REMOVED_GAUGE_IDS.has(g.id))
      .map((g) => {
        // oil_temp was replaced by AFR when the sender moved; the old gauge
        // config has no meaning against the new topic so take the default.
        if (g.id === "oil_temp" || g.topic === "lemons/OIL_TEMP") return afrDefault ?? g;
        if (g.id === "afr" && g.topic === "lemons/COMMANDED_EQUIV_RATIO") return afrDefault ?? g;
        return g;
      });
  }

  if (!data.calibration?.some((entry) => entry.topic === "lemons/gps/speed")) {
    const gpsSpeed = defaultAppConfig.calibration?.find((e) => e.topic === "lemons/gps/speed");
    if (gpsSpeed) data.calibration = [...(data.calibration ?? []), gpsSpeed];
  }

  const existingGaugeIds = new Set((data.gauges ?? []).map((g) => g.id));
  const missingGauges = defaultAppConfig.gauges.filter(
    (g) => MIGRATED_GAUGE_IDS.has(g.id) && !existingGaugeIds.has(g.id),
  );
  data.gauges = [...(data.gauges ?? []), ...missingGauges];

  const existingAlertIds = new Set((data.alerts ?? []).map((rule) => rule.id));
  const missingAlerts = defaultAlertRules.filter((rule) => !existingAlertIds.has(rule.id));
  data.alerts = [...(data.alerts ?? []), ...missingAlerts];

  if (typeof data.targetStintMinutes !== "number" || !Number.isFinite(data.targetStintMinutes) || data.targetStintMinutes < 0) {
    data.targetStintMinutes = DEFAULT_TARGET_STINT_MINUTES;
  }

  return data;
}
